import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";

// ---------------------------------------------------------------------------
// Screen 7 — States lived (RESPROP LIA Questionnaire p2). A searchable
// multi-select (chips) over the 50 U.S. states + Washington, D.C. (51 entries) —
// far friendlier on mobile than a 51-checkbox grid. Stored on the aide state as
// `statesLived: string[]` of 2-letter USPS codes ("DC" for Washington D.C.).
// No minimum — an empty selection is valid; we capture exactly what's chosen.
// ---------------------------------------------------------------------------

/** 50 states + D.C., in code order. `DC` maps to Washington D.C. */
export const US_STATES_WITH_DC: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "Washington D.C." },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

/** Canonical selection order (mirrors US_STATES_WITH_DC) for stable output. */
const CODE_ORDER: string[] = US_STATES_WITH_DC.map((s) => s.code);

/** Return `selected` re-ordered to the canonical code order. */
const sortByCanonicalOrder = (selected: string[]): string[] =>
  CODE_ORDER.filter((code) => selected.includes(code));

interface FormShape {
  statesLived: string[];
}

interface Props {
  /** Persisted selection (state.statesLived); defaults to []. */
  defaultValues?: string[];
  onSubmit: (data: string[]) => void | Promise<void>;
  isSaving?: boolean;
}

export const AideStatesLived: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm<FormShape>({
    defaultValues: { statesLived: defaultValues ?? [] },
  });

  return (
    <form
      onSubmit={handleSubmit((data) =>
        onSubmit(sortByCanonicalOrder(data.statesLived))
      )}
    >
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.statesLived.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.statesLived.subtitle")}
        </Typography>

        <Controller
          name="statesLived"
          control={control}
          render={({ field }) => {
            const selected = field.value ?? [];
            const selectedOptions = US_STATES_WITH_DC.filter((s) =>
              selected.includes(s.code)
            );
            return (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={US_STATES_WITH_DC}
                value={selectedOptions}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                onChange={(_, newValue) =>
                  field.onChange(
                    sortByCanonicalOrder(newValue.map((o) => o.code))
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      "affordable.application.liveInAide.statesLived.label"
                    )}
                    placeholder={
                      selectedOptions.length
                        ? ""
                        : (t(
                            "affordable.application.liveInAide.statesLived.placeholder"
                          ) as string)
                    }
                  />
                )}
              />
            );
          }}
        />
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
